import { NextPage } from 'next';
import { redirect } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { isDevelopmentEnv } from '@bts-web/utils-functions';
import { allTradeOperations } from '../CreateTrade/MainTradeController/utils/tradeOperations';
import {
  TradeBuyApplicationStatePushTan,
  TradeControllerVariants,
  TradeOperation,
} from '../utils';
import { AssetDetailsControllerWithSuspense } from '../../asset-details';
import {
  getCreatedSavingsPlanAction,
  AcceptSavingsPlanController,
} from '../AcceptSavingsPlan';
import { AcceptTradeController } from '../AcceptTrade/AcceptTradeController';
import { getTradeOfferData } from '../../common';
import { processPushTanApplicationState } from '../Reusable/ApplicationState/processPushTanApplicationState';
import { AcceptTradeControllerVariants } from '../AcceptTrade/types';
import { AcceptSwapController } from '../AcceptSwap/AcceptSwapController';
import { AcceptStakeController } from '../../staking/components/AcceptStakeController/AcceptStakeController';
import { AcceptUnstakeController } from '../../staking/components/AcceptUnstakeController/AcceptUnstakeController';
import { GenericErrorMessage } from '../../layouts/GenericErrorMessage/GenericErrorMessage';
import { AssetDetailTradeWrapper } from '../../layouts/AssetDetailTradeWrapper/AssetDetailTradeWrapper';
import { AcceptOrder } from '../AcceptOrder/AcceptOrder';
import { TransferStatusScreen } from '../Fiat/Wallet/TransferStatusScreen';
import { getServerFeatureFlag } from '../../statsig/sdk/server';

interface IAcceptOfferIndexPage {
  params: Promise<{
    assetId: string;
    operation: TradeControllerVariants;
    offerId: string;
    state: string;
  }>;
  searchParams: Promise<{
    targetAssetId: string;
    amount: string;
    amountFor: 'SOURCE' | 'TARGET';
    transferLink?: string;
    fiatTransactionId?: string;
  }>;
}

const AcceptOfferIndexPage: NextPage<IAcceptOfferIndexPage> = async ({
  params,
  searchParams,
}) => {
  /** @info There is no offerId involved for Swap Accept page, 'accept_swap' string is used as a placeholder for structural and route consistency purposes */
  const { assetId, operation, offerId, state } = await params;

  const { transferLink, amount, fiatTransactionId } = await searchParams;

  const feature_enableFiatTransfers = await getServerFeatureFlag(
    'feature_enableFiatTransfers',
  );

  if (
    allTradeOperations.includes(operation as TradeControllerVariants) === false
  ) {
    redirect(`/assets/${assetId}/trade/${allTradeOperations[0]}`);
  }

  // * accept savings plan
  if (operation === TradeOperation.savings) {
    const savingsPlanPreAcceptResponse = await getCreatedSavingsPlanAction({
      savingsPlanId: offerId,
    });

    if (!isDevelopmentEnv()) {
      if ((savingsPlanPreAcceptResponse?.errors?.length || 0) > 0) {
        redirect(NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId));
      }
    }

    const savingsPlan = savingsPlanPreAcceptResponse?.data
      ?.preAcceptSavingsPlan as NonNullable<
      typeof savingsPlanPreAcceptResponse.data.preAcceptSavingsPlan
    >;

    if (!savingsPlan) {
      return <GenericErrorMessage />;
    }

    return (
      <AssetDetailTradeWrapper
        startFocusValue={true}
        hideMobile="assetElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense assetId={assetId} />
        }
        TradeStateContent={
          <AcceptSavingsPlanController savingsPlanData={savingsPlan} />
        }
      />
    );
  }

  // * accept swap
  if (operation === TradeOperation.swap) {
    return (
      <AssetDetailTradeWrapper
        startFocusValue={true}
        hideMobile="assetElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense assetId={assetId} />
        }
        TradeStateContent={
          <AcceptSwapController
            sourceAssetId={assetId}
            searchParams={searchParams}
          />
        }
      />
    );
  }

  // * accept stake and unstake
  if (
    operation === TradeOperation.stake ||
    operation === TradeOperation.unstake
  ) {
    return (
      <AssetDetailTradeWrapper
        startFocusValue={true}
        hideMobile="assetElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense assetId={assetId} />
        }
        TradeStateContent={
          operation === 'stake' ? (
            <AcceptStakeController assetId={assetId} amount={amount} />
          ) : (
            <AcceptUnstakeController assetId={assetId} amount={amount} />
          )
        }
      />
    );
  }

  // * accept order
  if (operation === TradeOperation.order) {
    return (
      <AssetDetailTradeWrapper
        startFocusValue={true}
        hideMobile="assetElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense assetId={assetId} />
        }
        TradeStateContent={<AcceptOrder orderId={offerId} />}
      />
    );
  }

  // * accept buy or sell
  const tradeOfferData = await getTradeOfferData(offerId);

  const tradeOffer = tradeOfferData?.data?.tradeOffer ?? null;

  // Business error codes the BE returns instead of a terminal transfer status;
  // these are surfaced on the TransferStatusScreen, not redirected.
  const FIAT_TRANSFER_ERROR_CODES = [
    'trading.timeframe.elapsed',
    'deposit.funds.consumed',
    'fiat.transfer.in.progress',
    'trade.offer.accepted',
  ];

  const transferErrorCode = (tradeOfferData?.errors ?? []).find((code) =>
    FIAT_TRANSFER_ERROR_CODES.includes(code),
  );

  // * accept FIAT buy - when no offer is available to accept
  // - begin Open Banking flow OR
  // - continue polling for a terminal status
  if (
    operation === TradeOperation.buy &&
    feature_enableFiatTransfers &&
    !tradeOffer
  ) {
    const params = await searchParams;

    // Changing query params generates a new key string, forcing a full remount
    const key = JSON.stringify(params);

    // An error code that is not expected was thrown
    if ((tradeOfferData?.errors?.length ?? 0) > 0 && !transferErrorCode) {
      redirect(NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId));
    }

    // Resume entry (no transferLink): offer expired / funds consumed are
    // terminal, so redirect to deposit details instead of an error screen.
    // The other two codes stay with TransferStatusScreen below — it needs
    // them for live polling and the success handoff.
    if (
      transferErrorCode === 'trading.timeframe.elapsed' ||
      transferErrorCode === 'deposit.funds.consumed'
    ) {
      redirect(
        fiatTransactionId
          ? `${NEXT_ROUTES.TRANSACTIONS_DETAILS.replace('[id]', fiatTransactionId)}?transactionType=FIAT_DEPOSIT`
          : NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId),
      );
    }

    return (
      <AssetDetailTradeWrapper
        startFocusValue={true}
        hideMobile="assetElement"
        AssetDetailsContent={
          <AssetDetailsControllerWithSuspense assetId={assetId} />
        }
        TradeStateContent={
          <TransferStatusScreen
            key={key}
            assetId={assetId}
            offerId={offerId}
            transferLink={transferLink}
            initialErrors={transferErrorCode ? [transferErrorCode] : undefined}
          />
        }
      />
    );
  } else if (!feature_enableFiatTransfers && transferErrorCode) {
    redirect(
      fiatTransactionId
        ? `${NEXT_ROUTES.TRANSACTIONS_DETAILS.replace('[id]', fiatTransactionId)}?transactionType=FIAT_DEPOSIT`
        : NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId),
    );
  }

  if ((tradeOfferData?.errors?.length ?? 0) > 0) {
    redirect(NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId));
  }

  if (operation === TradeOperation.buy && !!state) {
    processPushTanApplicationState({
      variant: operation as TradeControllerVariants,
      assetId,
      state: state as TradeBuyApplicationStatePushTan,
    });
  }

  return (
    <AssetDetailTradeWrapper
      startFocusValue={true}
      hideMobile="assetElement"
      AssetDetailsContent={
        <AssetDetailsControllerWithSuspense assetId={assetId} />
      }
      TradeStateContent={
        <AcceptTradeController
          offerData={tradeOffer!}
          variant={operation as AcceptTradeControllerVariants}
        />
      }
    />
  );
};

export { AcceptOfferIndexPage };
